import type React from 'react';
import { vases } from '../../../data/vases';
import {
  DEFAULT_FONT_LIBRARY,
  getTypographyPreset,
  resolveTypographyPresetKey,
  resolveTypographyStyle,
  type DesignSystemTypography,
} from '../../../lib/typography';
import { resolveCollectionCardActionBindings } from '../../../lib/collectionCardActions';
import { resolveCollectionCardTemplate } from '../../../lib/collectionCardTemplates';
import { toWebpVariantUrl } from '../../../lib/optimizedImages';
import {
  findProductRecordById,
  resolveProductRecordDescription,
  resolveProductRecordFeaturedImage,
  resolveProductRecordImageAlt,
  resolveProductRecordModelPath,
  resolveProductRecordTitle,
  resolveProductSelectionPayload,
} from '../../../lib/productRecords';
import { resolveProductCardLinkedFields } from '../../../lib/productCardContentLinking';
import {
  collectionCardShadowToCss,
  hexToRgba,
  resolveCollectionActionTargetModelId,
  resolveCollectionCardImageFrameStyle,
} from '../collectionCardRendering';
import type {
  CanvasSectionBlock,
  HomepageDesignSystemSettings,
  HomepageSectionSettings,
} from '../sectionRegistry';

type ResolveProductCardRenderModelOptions = {
  block: CanvasSectionBlock;
  productRecords: any[];
  selectedProductIds: Set<number>;
  designSystemOverride?: HomepageDesignSystemSettings;
  settings?: HomepageSectionSettings;
  w: number;
  h: number;
  x: number;
  y: number;
  columnWidthPx: number;
  rowHeightPx: number;
  scaleMode: boolean;
  isAdmin: boolean;
  isInteractPreview: boolean;
  activeProjectionRows: number;
  layerSize: { width: number; height: number };
  fontLibrary?: Record<string, string>;
  typography: DesignSystemTypography;
  useFluidSectionTypography: boolean;
  hoveredProductCardId: string | null;
};

export function resolveProductCardRenderModel({
  block,
  productRecords,
  selectedProductIds,
  designSystemOverride,
  settings,
  w,
  h,
  x,
  y,
  columnWidthPx,
  rowHeightPx,
  scaleMode,
  isAdmin,
  isInteractPreview,
  activeProjectionRows,
  layerSize,
  fontLibrary = DEFAULT_FONT_LIBRARY,
  typography,
  useFluidSectionTypography,
  hoveredProductCardId,
}: ResolveProductCardRenderModelOptions) {
  const content =
    block.content && typeof block.content === 'object'
      ? (block.content as Record<string, unknown>)
      : {};
  const productId =
    Number.isFinite(content.modelReference) && Number(content.modelReference) > 0
      ? Number(content.modelReference)
      : Number(content.productId);
  const sourceProduct =
    findProductRecordById(productRecords, productId) ??
    resolveProductSelectionPayload(vases.find((vase) => vase.id === productId) ?? vases[0]);
  const templateId =
    typeof content.templateId === 'string' && content.templateId.trim().length > 0
      ? content.templateId.trim()
      : 'vase-card';
  const template = resolveCollectionCardTemplate(
    designSystemOverride?.collectionCardTemplates ?? settings?.collectionCardTemplates,
    templateId
  );
  const actions = resolveCollectionCardActionBindings(
    content,
    sourceProduct?.id ?? 1,
    String(content.modelPath ?? resolveProductRecordModelPath(sourceProduct) ?? '')
  );
  const selectedModelReference = resolveCollectionActionTargetModelId(
    actions.primaryAction,
    sourceProduct?.id ?? 1
  );
  const resolvedProduct =
    findProductRecordById(productRecords, selectedModelReference) ??
    resolveProductSelectionPayload(
      vases.find((vase) => vase.id === selectedModelReference) ?? sourceProduct ?? vases[0]
    );
  const linkedFields = resolveProductCardLinkedFields(content, resolvedProduct);
  const resolvedName = linkedFields.title.trim() || resolveProductRecordTitle(resolvedProduct) || 'Product';
  const resolvedDescription =
    linkedFields.description.trim() ||
    resolveProductRecordDescription(resolvedProduct) ||
    'Product description';
  const resolvedImage =
    linkedFields.image.trim() || resolveProductRecordFeaturedImage(resolvedProduct) || '';
  const resolvedImageSrc = toWebpVariantUrl(resolvedImage) ?? resolvedImage;
  const resolvedImageAlt =
    linkedFields.imageAlt.trim() ||
    resolveProductRecordImageAlt(resolvedProduct) ||
    'Product image';
  const resolvedProductModelPath = resolveProductRecordModelPath(resolvedProduct).trim();
  const resolvedModelPath =
    actions.secondaryAction.action === 'viewModel'
      ? actions.secondaryAction.useTargetOverride === true
        ? actions.secondaryAction.target.trim() ||
          resolvedProductModelPath ||
          String(content.modelPath ?? '').trim()
        : resolvedProductModelPath || String(content.modelPath ?? '').trim()
      : resolvedProductModelPath || String(content.modelPath ?? '').trim();
  const fallbackScale = Math.max(0.5, Math.min(1.25, Number(content.contentScale) || 1));
  const contentScaleX = Math.max(0.5, Math.min(1.25, Number(content.contentScaleX) || fallbackScale));
  const contentScaleY = Math.max(0.5, Math.min(1.25, Number(content.contentScaleY) || fallbackScale));
  const autoSizeResolved = content.autoSizeResolved !== false;
  const designCols = Math.max(1, Number(content.designCols) || Math.max(block.layout.w, 7));
  const designRows = Math.max(1, Number(content.designRows) || Math.max(block.layout.h, 8));
  const frameWidthPx = w * columnWidthPx;
  const frameHeightPx = h * rowHeightPx;
  const naturalCardWidthPx = designCols * columnWidthPx;
  const naturalCardHeightPx = designRows * rowHeightPx;
  const scaledCardWidthPx = naturalCardWidthPx * contentScaleX;
  const scaledCardHeightPx = naturalCardHeightPx * contentScaleY;
  const productCardWrapperStyle: React.CSSProperties = {
    left: `${(x - 1) * columnWidthPx}px`,
    top: `${(y - 1) * rowHeightPx}px`,
    width: `${scaleMode ? scaledCardWidthPx : frameWidthPx}px`,
    height: `${scaleMode ? scaledCardHeightPx : frameHeightPx}px`,
  };
  const productCardOverlayStyle: React.CSSProperties | undefined = scaleMode
    ? {
        left: 0,
        top: 0,
        width: `${scaledCardWidthPx}px`,
        height: `${scaledCardHeightPx}px`,
      }
    : undefined;
  const contentOffsetX = Number(content.contentOffsetX) || 0;
  const contentOffsetY = Number(content.contentOffsetY) || 0;
  const cropContentToFrame = isAdmin && !scaleMode && !isInteractPreview;
  const translateXPx = !scaleMode && cropContentToFrame ? contentOffsetX * columnWidthPx : 0;
  const translateYPx = !scaleMode && cropContentToFrame ? contentOffsetY * rowHeightPx : 0;
  const runtimeSnapshot = {
    scaleMode,
    autoSizeResolved,
    cropContentToFrame,
    section: {
      activeProjectionRows,
      layerWidthPx: layerSize.width,
      layerHeightPx: layerSize.height,
      columnWidthPx,
      rowHeightPx,
    },
    blockLayout: { x, y, w, h },
    designGrid: { designCols, designRows },
    contentTransform: {
      contentOffsetX,
      contentOffsetY,
      translateXPx,
      translateYPx,
      contentScaleX,
      contentScaleY,
      transformOrigin: 'top left',
    },
    geometryPx: {
      frameWidthPx,
      frameHeightPx,
      naturalCardWidthPx,
      naturalCardHeightPx,
      scaledCardWidthPx,
      scaledCardHeightPx,
      wrapperWidthPx: scaleMode ? scaledCardWidthPx : frameWidthPx,
      wrapperHeightPx: scaleMode ? scaledCardHeightPx : frameHeightPx,
      overlayWidthPx: scaleMode ? scaledCardWidthPx : frameWidthPx,
      overlayHeightPx: scaleMode ? scaledCardHeightPx : frameHeightPx,
    },
  };
  const isSelectedProduct = selectedProductIds.has(selectedModelReference);
  const titlePresetStyle = resolveTypographyStyle(
    getTypographyPreset(typography, resolveTypographyPresetKey(template.titlePresetKey, 'sectionHeading')),
    fontLibrary,
    { fluid: useFluidSectionTypography }
  );
  const descriptionPresetStyle = resolveTypographyStyle(
    getTypographyPreset(typography, resolveTypographyPresetKey(template.descriptionPresetKey, 'body')),
    fontLibrary,
    { fluid: useFluidSectionTypography }
  );
  const titleTypographyStyle: React.CSSProperties = {
    ...titlePresetStyle,
    fontFamily: fontLibrary[template.titleTypography.fontFamilyKey] ?? titlePresetStyle.fontFamily,
    fontSize: `${template.titleTypography.fontSize}px`,
    fontWeight: template.titleTypography.fontWeight,
    color: template.titleTypography.colour,
    fontStyle: template.titleTypography.fontStyle,
    textDecorationLine: template.titleTypography.textDecorationLine,
    textTransform: template.titleTypography.textTransform,
    lineHeight: String(template.titleTypography.lineHeight),
    letterSpacing: `${template.titleTypography.letterSpacing}px`,
  };
  const descriptionTypographyStyle: React.CSSProperties = {
    ...descriptionPresetStyle,
    fontFamily: fontLibrary[template.descriptionTypography.fontFamilyKey] ?? descriptionPresetStyle.fontFamily,
    fontSize: `${template.descriptionTypography.fontSize}px`,
    fontWeight: template.descriptionTypography.fontWeight,
    color: template.descriptionTypography.colour,
    fontStyle: template.descriptionTypography.fontStyle,
    textDecorationLine: template.descriptionTypography.textDecorationLine,
    textTransform: template.descriptionTypography.textTransform,
    lineHeight: String(template.descriptionTypography.lineHeight),
    letterSpacing: `${template.descriptionTypography.letterSpacing}px`,
  };
  const buttonTypographyStyle: React.CSSProperties = {
    fontFamily: fontLibrary[template.buttonTypography.fontFamilyKey] ?? descriptionPresetStyle.fontFamily,
    fontSize: `${template.buttonTypography.fontSize}px`,
    fontWeight: template.buttonTypography.fontWeight,
    fontStyle: template.buttonTypography.fontStyle,
    textDecorationLine: template.buttonTypography.textDecorationLine,
    textTransform: template.buttonTypography.textTransform,
    lineHeight: String(template.buttonTypography.lineHeight),
    letterSpacing: `${template.buttonTypography.letterSpacing}px`,
  };
  const rowOrder = template.rows.filter((row: string) => row !== 'buttons');
  const textAlignClass =
    template.textAlign === 'centre'
      ? 'items-center text-center'
      : template.textAlign === 'right'
        ? 'items-end text-right'
        : 'items-start text-left';
  const buttonJustifyClass =
    template.buttonStyle.alignment === 'centre'
      ? 'justify-center'
      : template.buttonStyle.alignment === 'left'
        ? 'justify-start'
        : '';
  const productCardImageStyle: React.CSSProperties = {
    width: `${template.imageSizePercent}%`,
    maxWidth: '100%',
    alignSelf: 'center',
  };
  const productCardImageFrameStyle: React.CSSProperties = {
    ...productCardImageStyle,
    ...resolveCollectionCardImageFrameStyle(template.imageAspect),
  };
  const productCardImageObjectPosition = `${template.imageFocalXPercent}% ${template.imageFocalYPercent}%`;
  const productCardImageOverlayStyle: React.CSSProperties | undefined =
    template.imageOverlayOpacity > 0
      ? { backgroundColor: hexToRgba(template.imageOverlayColour, template.imageOverlayOpacity) }
      : undefined;
  const cardBoxShadow = collectionCardShadowToCss(template);
  const hoverCardBoxShadow = template.hoverEffectEnabled
    ? collectionCardShadowToCss({
        cardShadowColour: template.cardShadowColour,
        cardShadowOpacity: template.hoverShadowOpacity,
        cardShadowX: template.cardShadowX,
        cardShadowY: template.hoverShadowY,
        cardShadowBlur: template.hoverShadowBlur,
        cardShadowSpread: template.cardShadowSpread,
      })
    : cardBoxShadow;
  const hoverAnimation = template.hoverAnimation ?? 'lift-shadow';
  const primaryButtonVariant = template.buttonStyle.primaryVariant ?? 'solid';
  const secondaryButtonVariant = template.buttonStyle.secondaryVariant ?? 'solid';
  const productCardDomId = `builder-product-card-${block.id}`;
  const productCardShadowDomId = `builder-product-card-shadow-${block.id}`;
  const productCardWrapperDomId = `builder-product-card-wrapper-${block.id}`;
  const productCardImageDomId = `builder-product-card-image-${block.id}`;
  const productCardDescriptionDomId = `builder-product-card-description-${block.id}`;
  const productCardButtonsDomId = `builder-product-card-buttons-${block.id}`;
  const isHoverActive = template.hoverEffectEnabled && hoveredProductCardId === block.id;
  const shouldRevealDescription =
    template.hoverEffectEnabled &&
    hoverAnimation === 'reveal' &&
    (template.hoverRevealTarget === 'description' || template.hoverRevealTarget === 'both');
  const shouldRevealButtons =
    template.hoverEffectEnabled &&
    hoverAnimation === 'reveal' &&
    (template.hoverRevealTarget === 'buttons' || template.hoverRevealTarget === 'both');
  const baseCardTransform = `translate(${translateXPx}px, ${translateYPx}px) scale(${contentScaleX}, ${contentScaleY})`;
  const hoverCardTransform =
    template.hoverEffectEnabled && hoverAnimation === 'lift-shadow'
      ? `translate(${translateXPx}px, ${translateYPx - template.hoverLiftY}px) scale(${contentScaleX}, ${contentScaleY})`
      : baseCardTransform;
  const hoverContainerTransform =
    template.hoverEffectEnabled && hoverAnimation === 'tilt'
      ? `translateY(-${template.hoverLiftY}px) rotate(${template.hoverTiltDeg}deg)`
      : 'none';
  const hoverShadowTransform =
    template.hoverEffectEnabled && hoverAnimation === 'lift-shadow'
      ? `translateY(-${template.hoverLiftY}px)`
      : 'none';
  const hoverGlowBoxShadow =
    template.hoverEffectEnabled && hoverAnimation === 'glow'
      ? [
          cardBoxShadow,
          `0 0 ${Math.max(8, template.hoverGlowSize)}px ${hexToRgba(
            template.hoverGlowColour,
            Math.max(template.hoverGlowOpacity, 0.08)
          )}`,
          `0 0 ${Math.max(16, template.hoverGlowSize * 1.8)}px ${hexToRgba(
            template.hoverGlowColour,
            Math.max(template.hoverGlowOpacity * 0.45, 0.04)
          )}`,
        ]
          .filter(Boolean)
          .join(', ')
      : hoverCardBoxShadow;

  return {
    actions,
    autoSizeResolved,
    baseCardTransform,
    buttonJustifyClass,
    buttonTypographyStyle,
    cardBoxShadow,
    hoverAnimation,
    hoverCardTransform,
    hoverContainerTransform,
    hoverGlowBoxShadow,
    hoverShadowTransform,
    isHoverActive,
    isSelectedProduct,
    naturalCardHeightPx,
    naturalCardWidthPx,
    primaryButtonVariant,
    productCardButtonsDomId,
    productCardDescriptionDomId,
    productCardDomId,
    productCardImageDomId,
    productCardImageFrameStyle,
    productCardImageObjectPosition,
    productCardImageOverlayStyle,
    productCardOverlayStyle,
    productCardShadowDomId,
    productCardWrapperDomId,
    productCardWrapperStyle,
    resolvedDescription,
    resolvedImage,
    resolvedImageAlt,
    resolvedImageSrc,
    resolvedModelPath,
    resolvedName,
    resolvedProduct,
    rowOrder,
    runtimeSnapshot,
    secondaryButtonVariant,
    selectedModelReference,
    shouldRevealButtons,
    shouldRevealDescription,
    template,
    textAlignClass,
    titleTypographyStyle,
    descriptionTypographyStyle,
  };
}
